/**
 * Playtomic Reservations - View and manage Playtomic reservations
 * Works for both Admin and Club Owner back offices
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Clock, User, DollarSign, ArrowLeft, RefreshCw,
  CheckCircle, XCircle, AlertCircle, CreditCard, ChevronLeft,
  ChevronRight, Filter, Search, Lock, Unlock, BarChart3,
  Phone, Mail, Users, MapPin, Loader2, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { playtomicAPI } from '../../services/api';
import { toast } from 'sonner';

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-500', label: 'Pendiente' },
    confirmed: { bg: 'bg-blue-500/20', text: 'text-blue-500', label: 'Confirmado' },
    paid: { bg: 'bg-green-500/20', text: 'text-green-500', label: 'Pagado' },
    cancelled: { bg: 'bg-red-500/20', text: 'text-red-500', label: 'Cancelado' },
    no_show: { bg: 'bg-gray-500/20', text: 'text-gray-500', label: 'No Show' },
  };
  
  const { bg, text, label } = config[status] || config.pending;
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
      {label}
    </span>
  );
};

// Payment status badge
const PaymentBadge = ({ status }) => {
  const config = {
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-500', icon: AlertCircle, label: 'Por cobrar' },
    paid_online: { bg: 'bg-green-500/20', text: 'text-green-500', icon: CheckCircle, label: 'Pagado online' },
    paid_at_club: { bg: 'bg-green-500/20', text: 'text-green-500', icon: CheckCircle, label: 'Pagado en club' },
    refunded: { bg: 'bg-red-500/20', text: 'text-red-500', icon: XCircle, label: 'Reembolsado' },
  };
  
  const { bg, text, icon: Icon, label } = config[status] || config.pending;
  
  return (
    <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

// Reservation card component
const ReservationCard = ({ reservation, onProcessPayment }) => {
  const isPending = reservation.payment_status === 'pending';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-xl border border-border p-4 hover:border-primary/50 transition-colors"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-muted-foreground">{reservation.id}</span>
            <StatusBadge status={reservation.status} />
          </div>
          <h3 className="font-semibold">{reservation.player_name}</h3>
        </div>
        <PaymentBadge status={reservation.payment_status} />
      </div>
      
      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
        <div className="flex items-center gap-2 text-muted-foreground">
          <MapPin className="w-4 h-4" />
          {reservation.court_name}
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="w-4 h-4" />
          {reservation.start_time} - {reservation.end_time}
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Users className="w-4 h-4" />
          {reservation.num_players} jugadores
        </div>
        <div className="flex items-center gap-2 font-semibold text-primary">
          <DollarSign className="w-4 h-4" />
          ${reservation.price?.toFixed(2)} MXN
        </div>
      </div>
      
      {reservation.player_phone && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
          <Phone className="w-3 h-3" />
          {reservation.player_phone}
        </div>
      )}
      
      {isPending && reservation.status !== 'cancelled' && (
        <Button 
          onClick={() => onProcessPayment(reservation)}
          className="w-full mt-2"
          size="sm"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          Procesar Pago
        </Button>
      )}
    </motion.div>
  );
};

// Payment modal
const PaymentModal = ({ reservation, onClose, onConfirm }) => {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amount, setAmount] = useState(reservation?.price || 0);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await onConfirm({
        payment_method: paymentMethod,
        amount,
        notes: notes || undefined
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card rounded-2xl border border-border shadow-xl"
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">Procesar Pago</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Reservation Info */}
          <div className="bg-secondary/50 rounded-lg p-3 space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Reservación:</span>
              <span className="font-mono">{reservation?.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cliente:</span>
              <span>{reservation?.player_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cancha:</span>
              <span>{reservation?.court_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Horario:</span>
              <span>{reservation?.start_time} - {reservation?.end_time}</span>
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <Label>Método de Pago</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {[
                { id: 'cash', label: 'Efectivo', icon: DollarSign },
                { id: 'card', label: 'Tarjeta', icon: CreditCard },
                { id: 'transfer', label: 'Transferencia', icon: RefreshCw },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPaymentMethod(id)}
                  className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                    paymentMethod === id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Amount */}
          <div>
            <Label>Monto a Cobrar</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                className="pl-9"
                step="0.01"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label>Notas (opcional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observaciones..."
              className="mt-1"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={processing}>
              {processing ? 'Procesando...' : `Cobrar $${amount.toFixed(2)}`}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Block Slot Modal
const BlockSlotModal = ({ courts, onClose, onConfirm }) => {
  const [courtId, setCourtId] = useState('');
  const [courtName, setCourtName] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [reason, setReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleCourtChange = (e) => {
    const id = e.target.value;
    setCourtId(id);
    const court = courts.find(c => c.id === id);
    setCourtName(court?.name || '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!courtId) {
      toast.error('Selecciona una cancha');
      return;
    }
    setProcessing(true);
    try {
      await onConfirm({
        court_id: courtId,
        court_name: courtName,
        date,
        start_time: startTime,
        end_time: endTime,
        reason: reason || 'Bloqueado desde POS'
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card rounded-2xl border border-border shadow-xl"
      >
        <div className="p-4 border-b border-border flex items-center justify-between bg-orange-500/10">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-orange-500" />
            <h2 className="font-display font-bold text-lg">Bloquear Horario</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <Label>Cancha *</Label>
            <select
              value={courtId}
              onChange={handleCourtChange}
              className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
              required
            >
              <option value="">Seleccionar cancha...</option>
              {courts.map((court) => (
                <option key={court.id} value={court.id}>{court.name}</option>
              ))}
            </select>
          </div>

          <div>
            <Label>Fecha *</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Hora Inicio *</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="mt-1"
                required
              />
            </div>
            <div>
              <Label>Hora Fin *</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="mt-1"
                required
              />
            </div>
          </div>

          <div>
            <Label>Motivo</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Mantenimiento, Evento privado..."
              className="mt-1"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700" disabled={processing}>
              {processing ? 'Bloqueando...' : 'Bloquear Horario'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Main Component
export default function PlaytomicReservations({ isAdmin = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [summary, setSummary] = useState({});
  const [courts, setCourts] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [paymentReservation, setPaymentReservation] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [reservationsRes, courtsRes, blockedRes] = await Promise.all([
        playtomicAPI.getReservations({ date: selectedDate, status: statusFilter || undefined, payment_status: paymentFilter || undefined }),
        playtomicAPI.getCourts(),
        playtomicAPI.getBlockedSlots({ date: selectedDate })
      ]);
      
      setReservations(reservationsRes.data.reservations || []);
      setSummary(reservationsRes.data.summary || {});
      setCourts(courtsRes.data.courts || []);
      setBlockedSlots(blockedRes.data.blocked_slots || []);
    } catch (err) {
      console.error('Error fetching Playtomic data:', err);
      toast.error('Error cargando datos de Playtomic');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, statusFilter, paymentFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await playtomicAPI.sync();
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error('Error sincronizando');
    } finally {
      setSyncing(false);
    }
  };

  const handleProcessPayment = async (data) => {
    try {
      await playtomicAPI.processPayment(paymentReservation.id, data);
      toast.success('Pago procesado correctamente');
      setPaymentReservation(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error procesando pago');
      throw err;
    }
  };

  const handleBlockSlot = async (data) => {
    try {
      await playtomicAPI.blockSlot(data);
      toast.success('Horario bloqueado');
      setShowBlockModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error bloqueando horario');
      throw err;
    }
  };

  const handleUnblockSlot = async (slotId) => {
    if (!window.confirm('¿Desbloquear este horario?')) return;
    try {
      await playtomicAPI.unblockSlot(slotId);
      toast.success('Horario desbloqueado');
      fetchData();
    } catch (err) {
      toast.error('Error desbloqueando');
    }
  };

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate.toISOString().split('T')[0]);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  // Filter reservations by search
  const filteredReservations = reservations.filter(r => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      r.player_name?.toLowerCase().includes(search) ||
      r.id?.toLowerCase().includes(search) ||
      r.court_name?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(isAdmin ? '/admin' : '/club')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-display font-bold text-lg">Playtomic</h1>
                <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">DEMO</span>
              </div>
              <p className="text-xs text-muted-foreground">Reservaciones del día</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleSync} disabled={syncing}>
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              Sincronizar
            </Button>
            <Button variant="outline" onClick={() => setShowBlockModal(true)}>
              <Lock className="w-4 h-4 mr-2" />
              Bloquear
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Date Navigation */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={() => changeDate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="text-center">
            <p className="font-display font-bold text-lg capitalize">{formatDate(selectedDate)}</p>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="text-xs text-muted-foreground bg-transparent border-none cursor-pointer"
            />
          </div>
          <Button variant="ghost" size="icon" onClick={() => changeDate(1)}>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-card rounded-xl border border-border p-4 text-center">
            <Calendar className="w-6 h-6 mx-auto mb-2 text-primary" />
            <p className="text-2xl font-bold">{summary.total || 0}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="bg-blue-500/10 rounded-xl p-4 text-center">
            <CheckCircle className="w-6 h-6 mx-auto mb-2 text-blue-500" />
            <p className="text-2xl font-bold text-blue-500">{summary.confirmed || 0}</p>
            <p className="text-xs text-muted-foreground">Confirmados</p>
          </div>
          <div className="bg-green-500/10 rounded-xl p-4 text-center">
            <DollarSign className="w-6 h-6 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold text-green-500">{summary.paid || 0}</p>
            <p className="text-xs text-muted-foreground">Pagados</p>
          </div>
          <div className="bg-yellow-500/10 rounded-xl p-4 text-center">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-yellow-500" />
            <p className="text-2xl font-bold text-yellow-500">{summary.pending_payment || 0}</p>
            <p className="text-xs text-muted-foreground">Por cobrar</p>
          </div>
          <div className="bg-primary/10 rounded-xl p-4 text-center">
            <BarChart3 className="w-6 h-6 mx-auto mb-2 text-primary" />
            <p className="text-2xl font-bold">${(summary.total_revenue || 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Cobrado</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre, ID, cancha..."
              className="pl-9"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 px-3 rounded-md border border-input bg-background text-sm"
          >
            <option value="">Todos los estados</option>
            <option value="pending">Pendiente</option>
            <option value="confirmed">Confirmado</option>
            <option value="paid">Pagado</option>
            <option value="cancelled">Cancelado</option>
          </select>
          <select
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            className="h-10 px-3 rounded-md border border-input bg-background text-sm"
          >
            <option value="">Todos los pagos</option>
            <option value="pending">Por cobrar</option>
            <option value="paid_online">Pagado online</option>
            <option value="paid_at_club">Pagado en club</option>
          </select>
        </div>

        {/* Blocked Slots */}
        {blockedSlots.length > 0 && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
            <h3 className="font-semibold flex items-center gap-2 mb-3">
              <Lock className="w-4 h-4 text-orange-500" />
              Horarios Bloqueados ({blockedSlots.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {blockedSlots.map((slot) => (
                <div key={slot.id} className="flex items-center gap-2 bg-background rounded-lg px-3 py-2 text-sm">
                  <span className="font-medium">{slot.court_name}</span>
                  <span className="text-muted-foreground">{slot.start_time} - {slot.end_time}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleUnblockSlot(slot.id)}
                  >
                    <Unlock className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reservations Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredReservations.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No hay reservaciones para este día</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredReservations.map((reservation) => (
              <ReservationCard
                key={reservation.id}
                reservation={reservation}
                onProcessPayment={() => setPaymentReservation(reservation)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {paymentReservation && (
          <PaymentModal
            reservation={paymentReservation}
            onClose={() => setPaymentReservation(null)}
            onConfirm={handleProcessPayment}
          />
        )}
        {showBlockModal && (
          <BlockSlotModal
            courts={courts}
            onClose={() => setShowBlockModal(false)}
            onConfirm={handleBlockSlot}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
